import { IAnalyticsAdapter } from '@/HostApp/types/AnalyticsModule';
import { GaUaEcommerceEvents } from '@/HostApp/services/Analytics/Adapters/OldEcommerceEvents';
import { Ga4EcommerceEvents } from '@/HostApp/services/Analytics/Adapters/Ga4EcommerceEvents';
import { GaUaDataHolderName } from '@/HostApp/services/Analytics/Adapters/GaUaDataHolderName';

const timeEvents: string[] = ['panelReady', 'navigationResponded', 'contextSent', 'viewShown'];

export const SkipTransformationErrorMsg = 'UA-eCommerce event was not configured for GA4 data-transformations!';

let instance: GoogleAnalyticsAdapter | null = null;
export default class GoogleAnalyticsAdapter implements IAnalyticsAdapter {
	private readonly cbPanel = 'UX1Panel';
	private readonly hasAnalytics: boolean = true;

	static getInstance(): GoogleAnalyticsAdapter {
		return instance || new GoogleAnalyticsAdapter();
	}

	constructor() {
		if (!window.dataLayer && !window.CustomAnalyticsProcessor) {
			// eslint-disable-next-line no-console
			console.info(`Analytics processing is not configured!`);
			this.hasAnalytics = false;
		}

		instance = this;
	}

	private pushAnalytics(data: Analytics.IGAEvent | unknown): void {
		if (window.CustomAnalyticsProcessor) {
			window.CustomAnalyticsProcessor.pushAnalytics(data);

			return;
		}

		window.dataLayer.push(data as Analytics.IGAEvent);
	}

	public pushData(data: Analytics.IGAEvent): void {
		if (!this.hasAnalytics) {
			return;
		}

		if ('eventValue' in data && timeEvents.includes(data.event) && data.eventValue && data.eventValue > 600000) {
			return;
		}

		data.eventCategory = data.eventCategory || data.event;
		data.eventAction = data.eventAction || data.event;
		data.cb_panel = this.cbPanel;
		data.dimension13 = this.cbPanel;

		const transformedData = this.safeDataTransformUaEcommerceEvent(data);

		performance.mark(transformedData.event);

		this.pushAnalytics(transformedData);
	}

	public pushException(viewId: string, viewLabel: string, message: string): void {
		if (!this.hasAnalytics) {
			return;
		}

		const eventName = 'exception';

		const data = {
			eventCategory: eventName,
			event: eventName,
			eventAction: viewLabel,
			eventLabel: viewId,
			dimension10: message,
			cb_panel: this.cbPanel,
			dimension13: this.cbPanel
		};

		this.pushAnalytics(data);
	}

	private safeDataTransformUaEcommerceEvent(data: Analytics.IGAEvent): Analytics.IGAEvent {
		try {
			return this.dataTransformUaEcommerceEvent(data);
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('Failed UA-eCommerce event data-transformations to GA4', data, e);
			return data;
		}
	}

	protected dataTransformUaEcommerceEvent(data: Analytics.IGAEvent): Analytics.IGAEvent {
		if (this.skipDataTransform(data)) {
			return data;
		}

		switch (data.event) {
			case GaUaEcommerceEvents.addToCart:
				return this.toGa4Ecommerce(data, Ga4EcommerceEvents.add_to_cart, GaUaDataHolderName.add);
			case GaUaEcommerceEvents.Checkout:
				return this.toGaEcommerceFromCheckout(data);
			case GaUaEcommerceEvents.checkoutOption:
				return this.toPlainCheckoutOptionEvent(data, GaUaDataHolderName.checkout_option);
			case GaUaEcommerceEvents.detail:
				return this.toGa4Ecommerce(data, Ga4EcommerceEvents.view_item, GaUaDataHolderName.detail);
			case GaUaEcommerceEvents.Impressions:
				return this.toGa4Ecommerce(
					data,
					Ga4EcommerceEvents.view_item_list,
					GaUaDataHolderName.impressions,
					true
				);
			case GaUaEcommerceEvents.productClick:
				return this.toGa4Ecommerce(data, Ga4EcommerceEvents.select_item, GaUaDataHolderName.click);
			case GaUaEcommerceEvents.removeFromCart:
				return this.toGa4Ecommerce(data, Ga4EcommerceEvents.remove_from_cart, GaUaDataHolderName.remove);
			default:
				return this.skipGa4EcommerceDataTransformation(data);
		}
	}

	protected skipDataTransform(data: Analytics.IGAEvent): boolean {
		// Ignore none eCommerce events.
		if (!data.ecommerce) {
			return true;
		}
		// Ignore GA4 eCommerce format events.
		if (data.ecommerce.items) {
			return true;
		}

		return false;
	}

	private skipGa4EcommerceDataTransformation(data: Analytics.IGAEvent): Analytics.IGAEvent {
		// eslint-disable-next-line no-console
		console.error(SkipTransformationErrorMsg, data);
		return data;
	}

	private toGaEcommerceFromCheckout(data: Analytics.IGAEvent): Analytics.IGAEvent {
		if ((data.ecommerce as Analytics.IEcommerce)?.checkout) {
			return this.toGa4Ecommerce(data, Ga4EcommerceEvents.begin_checkout, GaUaDataHolderName.checkout);
		} else if ((data.ecommerce as Analytics.IEcommerce)?.purchase) {
			return this.toGa4Ecommerce(data, Ga4EcommerceEvents.purchase, GaUaDataHolderName.purchase);
		}
		return this.skipGa4EcommerceDataTransformation(data);
	}

	private toGa4Ecommerce(
		data: Analytics.IGAEvent,
		eventName: string,
		propName: string,
		omitProductsProp: boolean = false
	): Analytics.IGAEvent {
		const oldECommerce = data.ecommerce as Analytics.IEcommerce;
		if (!oldECommerce) {
			return data;
		}

		data.event = eventName;

		const propsHolder = this.retrievePropsObj(propName, oldECommerce);
		const actionField = propsHolder?.actionField;
		const products = this.retrieveProducts(propsHolder, omitProductsProp);

		const items = this.mapProductsToItems(actionField?.list, products);
		// The revenue should be a total transaction value (incl. tax and shipping).
		// Let's use it as the monetary value of the event if provided.
		const revenue = actionField?.revenue ?? 0;
		let value: number;
		if (revenue > 0) {
			value = revenue;
		} else {
			// The revenue is empty in most cases, but the value property is required for eCommerce GA4 events.
			// Let's calculate on our own.
			value = items.reduce((n, { price, quantity }) => {
				return n + (price ?? 0) * (quantity ?? 0);
			}, 0);
		}

		const eCommerce: Analytics.IEcommerceGA4 = {
			currency: oldECommerce.currencyCode,
			transaction_id: actionField?.id,
			value,
			items
		};

		this.setGlobalEventPropsFromActionField(actionField, data);

		data.ecommerce = eCommerce;
		return data;
	}

	private retrievePropsObj(
		propName: string,
		oldECommerce: Analytics.IEcommerce
	): Analytics.IProps | Analytics.IActionProp {
		return oldECommerce[propName] as Analytics.IProps | Analytics.IActionProp;
	}

	private retrieveProducts(
		productsHolder: Analytics.IProps,
		omitProductsProp: boolean = false
	): Analytics.IProduct[] | undefined {
		if (omitProductsProp) {
			return productsHolder as unknown as Analytics.IProduct[];
		}

		return productsHolder?.products;
	}

	protected mapProductsToItems(
		itemListName: string | undefined,
		products: Analytics.IProduct[] | undefined
	): Analytics.IItem[] {
		if (!products) {
			return [];
		}
		if (!Array.isArray(products)) {
			return [];
		}

		return products.map((product): Analytics.IItem => {
			return {
				item_list_name: product.list ?? (itemListName as string),
				item_id: product.id,
				item_name: product.name,
				item_category: product.category,
				item_variant: product.variant,
				index: product.position,
				price: this.parseFloatSafe(product.price),
				quantity: product.quantity
			};
		});
	}

	protected parseFloatSafe(price: unknown): number | undefined {
		if (typeof price === 'number') {
			return price;
		}
		if (typeof price === undefined) {
			return undefined;
		}
		const result = parseFloat(price as string);

		return isNaN(result) ? undefined : result;
	}

	private toPlainCheckoutOptionEvent(data: Analytics.IGAEvent, propName: string): Analytics.IGAEvent {
		const oldECommerce = data.ecommerce as Analytics.IEcommerce;
		if (!oldECommerce) {
			return data;
		}
		data.ecommerce = undefined;

		const propsHolder = this.retrievePropsObj(propName, oldECommerce);
		this.setGlobalEventPropsFromActionField(propsHolder?.actionField, data);
		return data;
	}

	private setGlobalEventPropsFromActionField(actionField: Analytics.IActionInfo, data: Analytics.IGAEvent) {
		if (!actionField) {
			return;
		}
		data.action_step = actionField?.step;
		data.action_option = actionField?.option;
	}
}
